// Presentation-layer helpers for the attachment markers the daemon bakes into a
// timeline `user_message`.
//
// `promptInputToText` (packages/server/src/server/cyborg/agent-attachments.ts)
// renders every non-text prompt block as a literal marker — "[image]" for an
// inlined image block, "[Attached file: name (type, size)]" for a reference —
// blank-line separated after the typed text. That marker is REQUIRED server-side
// (text-only providers get the reference instead of the bytes, and the
// cross-daemon `rawPrompt` forward carries the same string), so it is NOT the
// server's job to drop it. The UI must not show it: the transcript renders the
// attachment itself.
//
// Both helpers below are pure and stringly-typed on purpose so the dedup path
// (state.svelte.ts) and the transcript (AgentStreamView.svelte) drive off the
// SAME definition of "this is a server-injected attachment marker".

// Exactly the two shapes promptInputToText / buildTextOnlyPrompt emit. Anything
// else (a fenced file excerpt, a page body) is deliberately NOT a marker — it is
// real content and must keep its own bubble rather than be silently swallowed.
// Since #3642 the reference line carries only server facts and the uploader's name/type
// follow it in a data fence ("the attached file's name and type"); both shapes are markers.
const ATTACHMENT_MARKER_RE =
  /^(?:\[image\]|\[Attached file:[^\]]*\]|\[Attached file:[^\]\n]*\]\n\[Untrusted content from the attached file's name and type[^\n]*\]\nBEGIN UNTRUSTED ([0-9a-f]{16})\n[\s\S]*?\nEND UNTRUSTED \1)$/;

export function isAttachmentMarkerBlock(block: string): boolean {
  return ATTACHMENT_MARKER_RE.test(block.trim());
}

// Drop the TRAILING marker blocks the daemon appended, keeping everything the
// human actually typed. Trailing-only: a "[image]" someone typed in the middle
// of their own sentence is their text, not our marker.
export function stripAttachmentMarkers(text: string): string {
  const blocks = text.split("\n\n");
  let end = blocks.length;
  while (end > 0 && isAttachmentMarkerBlock(blocks[end - 1])) end -= 1;
  return blocks.slice(0, end).join("\n\n").trimEnd();
}

// Is `echo` the server's echo of an optimistic prompt that carried attachments?
// True only when the echo is `optimistic` plus a tail made of NOTHING but
// attachment markers — the same "echo is a superset of the optimistic entry"
// shape CYBORG-309 already handles for staged /pages, kept deliberately narrow
// so two genuinely different prompts inside one turn are never collapsed.
export function echoIsAttachmentSuperset(optimistic: string, echo: string): boolean {
  if (!echo.startsWith(optimistic)) return false;
  const tail = echo.slice(optimistic.length);
  const blocks = tail
    .split("\n\n")
    .map((b) => b.trim())
    .filter((b) => b.length > 0);
  // No tail at all ⇒ identical text, which the plain `===` check already owns.
  if (blocks.length === 0) return false;
  return blocks.every(isAttachmentMarkerBlock);
}
